import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { Epic, UnassignedStory } from './types.js';

export function useEpics(projectId: string) {
  return useQuery({
    queryKey: ['epics', projectId],
    queryFn: () => apiFetch<{ epics: Epic[] }>(`/projects/${projectId}/epics`).then((r) => r.epics),
  });
}

/** Invalidate everything an epic change can affect: the list, stories, dashboard. */
function invalidateEpicViews(qc: ReturnType<typeof useQueryClient>, projectId: string): void {
  void qc.invalidateQueries({ queryKey: ['epics', projectId] });
  void qc.invalidateQueries({ queryKey: ['stories', projectId] });
  void qc.invalidateQueries({ queryKey: ['unassigned-stories', projectId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', projectId] });
}

export function useCreateEpic(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiFetch<{ epic: Epic }>(`/projects/${projectId}/epics`, {
        method: 'POST',
        body: { name },
      }).then((r) => r.epic),
    onSuccess: () => invalidateEpicViews(qc, projectId),
  });
}

export function useRenameEpic(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { epicId: string; name: string }) =>
      apiFetch<{ epic: Epic }>(`/projects/${projectId}/epics/${input.epicId}`, {
        method: 'PATCH',
        body: { name: input.name },
      }).then((r) => r.epic),
    onSuccess: () => invalidateEpicViews(qc, projectId),
  });
}

export function useDeleteEpic(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (epicId: string) =>
      apiFetch<{ ok: true }>(`/projects/${projectId}/epics/${epicId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateEpicViews(qc, projectId),
  });
}

export function useUnassignedStories(projectId: string) {
  return useQuery({
    queryKey: ['unassigned-stories', projectId],
    queryFn: () =>
      apiFetch<{ stories: UnassignedStory[] }>(
        `/projects/${projectId}/epics/unassigned-stories`,
      ).then((r) => r.stories),
  });
}

export function useAssignStoriesToEpic(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { epicId: string; storyIds: string[] }) =>
      apiFetch<{ ok: true }>(`/projects/${projectId}/epics/${input.epicId}/stories`, {
        method: 'POST',
        body: { storyIds: input.storyIds },
      }),
    onSuccess: () => invalidateEpicViews(qc, projectId),
  });
}
